/** Scheda del claim: stati fino al recupero, modalità di recupero, recuperato, blocco pagamento, red flag collegato (via proposta). */
import { App, Button, Card, Checkbox, Col, Input, InputNumber, Modal, Row, Select, Space } from "antd";
import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { ANCORA } from "@qa/shared";
import { STATI_CLAIM, VALUTE, type ClaimDto, type ModificaClaimInput, type PaginaDto, type PropostaRigaDto, type SegnalazioneRigaDto, type StatoClaim } from "@qa/shared";
import { api } from "../../api/client";
import { fmtImporto, numFormatter, numParser } from "../../lib/format";
import { usePuo } from "../../lib/permessi";
import { useAggiorna } from "../../lib/query";
import { messaggioErrore } from "../../i18n/errori";
import { SelectLista } from "../../components/campi";
import { Miss, PageHead, Processo, Prop, Ro, Sev, Stato, StatoClaimTag, StatoRf, Storico, Vuoto } from "../../components/ui";
import { palette } from "../../theme";

const ORDINE: Record<StatoClaim, number> = { aperto: 1, notificato: 2, in_trattativa: 3, accettato: 4, contestato: 4, recuperato: 5 };

type Bozza = Required<Pick<ModificaClaimInput, "brand" | "problema" | "risoluzione" | "importo" | "valuta" | "stato" | "modalitaRecupero" | "importoRecuperato" | "bloccoPagamento" | "note">>;
const daClaim = (c: ClaimDto): Bozza => ({
  brand: c.brand,
  problema: c.problema,
  risoluzione: c.risoluzione,
  importo: c.importo,
  valuta: c.valuta,
  stato: c.stato,
  modalitaRecupero: c.modalitaRecupero,
  importoRecuperato: c.importoRecuperato,
  bloccoPagamento: c.bloccoPagamento,
  note: c.note,
});

function ProponiCollegamento({ c, altro }: { c: ClaimDto; altro: boolean }) {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const aggiorna = useAggiorna();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [rf, setRf] = useState<string | null>(null);
  const [verifica, setVerifica] = useState(false);
  const r = useQuery({ queryKey: ["segnalazioni", "scelta", q], queryFn: () => api.get<PaginaDto<SegnalazioneRigaDto>>("/redflag/segnalazioni", { q: q || undefined, limit: 20 }), enabled: open });
  const m = useMutation({
    mutationFn: () => api.post<PropostaRigaDto>("/proposte", { tipo: "collegamento_claim", contenuto: { claimId: c.id, segnalazioneId: rf, daVerificare: verifica } }),
    onSuccess: () => {
      message.success(t("redflag.scheda.propostaInviata"));
      void aggiorna();
      setOpen(false);
    },
    onError: (e) => message.error(messaggioErrore(e)),
  });
  return (
    <>
      <Button size="small" onClick={() => setOpen(true)}>
        {altro ? t("claim.scheda.proponiAltro") : t("claim.scheda.proponi")}
      </Button>
      <Modal open={open} onCancel={() => setOpen(false)} title={t("claim.scheda.proponiTitolo")} okText={t("redflag.scheda.proponi")} cancelText={t("azioni.annulla")} okButtonProps={{ disabled: !rf }} confirmLoading={m.isPending} onOk={() => m.mutate()}>
        <Space direction="vertical" style={{ width: "100%" }}>
          <div className="muted small">{t("claim.scheda.segnalazione")}</div>
          <Select
            showSearch
            filterOption={false}
            onSearch={setQ}
            value={rf ?? undefined}
            onChange={setRf}
            loading={r.isFetching}
            placeholder={t("claim.scheda.segnalazioneCerca")}
            style={{ width: "100%" }}
            options={(r.data?.righe ?? []).map((x) => ({ value: x.id, label: `${x.codice} · ${x.cliente}${x.progetto ? ` · ${x.progetto}` : ""}` }))}
          />
          <Checkbox checked={verifica} onChange={(e) => setVerifica(e.target.checked)}>
            {t("claim.scheda.daVerificare")}
          </Checkbox>
          <span className="muted small">{t("redflag.scheda.proponiNota")}</span>
        </Space>
      </Modal>
    </>
  );
}

export default function ClaimSchedaPage() {
  const { id = "" } = useParams();
  const { t } = useTranslation();
  const { message } = App.useApp();
  const puo = usePuo();
  const aggiorna = useAggiorna();
  const q = useQuery({ queryKey: ["claim", id], queryFn: () => api.get<ClaimDto>(`/claim/${id}`) });
  const iniziale = useMemo(() => (q.data ? daClaim(q.data) : null), [q.data]);
  const [b, setB] = useState<Bozza | null>(null);
  useEffect(() => setB(iniziale), [iniziale]);
  const m = useMutation({
    mutationFn: (x: Bozza) => api.patch<ClaimDto>(`/claim/${id}`, { ...x, brand: x.brand || null, note: x.note || null }),
    onSuccess: () => {
      message.success(t("claim.scheda.salvato"));
      void aggiorna();
    },
    onError: (e) => message.error(messaggioErrore(e)),
  });
  return (
    <Stato q={q} righe={12}>
      {(c) => {
        if (!b) return null;
        const ro = !puo("claim.gestisci");
        const cambiato = JSON.stringify(b) !== JSON.stringify(iniziale);
        const set = <K extends keyof Bozza>(k: K, v: Bozza[K]) => setB({ ...b, [k]: v });
        const pos = ORDINE[c.stato];
        const st = (n: number) => (c.stato === "recuperato" || n < pos ? "done" : n === pos ? "cur" : "") as "done" | "cur" | "";
        const rf = c.segnalazioneDettaglio;
        return (
          <>
            <PageHead
              crumbs={[{ label: t("portale.nome"), to: "/" }, { label: t("menu.gruppoClaim"), to: "/claim" }, { label: c.codice }]}
              title={`${c.codice} · ${c.fornitoreTesto}`}
              sub={[c.brand, c.problema].filter(Boolean).join(" · ")}
              extra={
                <>
                  <StatoClaimTag s={c.stato} />
                  {!ro && (
                    <Select
                      value={b.stato}
                      onChange={(v) => set("stato", v)}
                      style={{ width: 220 }}
                      aria-label={t("claim.scheda.cambiaStato")}
                      options={STATI_CLAIM.map((s) => ({ value: s, label: t(`dominio.statoClaim.${s}`) }))}
                    />
                  )}
                </>
              }
            />
            <Processo
              passi={[
                { titolo: t("dominio.statoClaim.aperto"), chi: t("redflag.processo.p4r"), stato: st(1) },
                { titolo: t("dominio.statoClaim.notificato"), stato: st(2) },
                { titolo: t("dominio.statoClaim.in_trattativa"), stato: st(3) },
                { titolo: `${t("dominio.statoClaim.accettato")} / ${t("dominio.statoClaim.contestato")}`, stato: st(4) },
                { titolo: t("dominio.statoClaim.recuperato"), stato: st(5) },
              ]}
            />
            <Row gutter={[16, 16]}>
              <Col xs={24} xl={16}>
                <Card size="small" title={t("claim.scheda.dati")}>
                  <dl className="kv">
                    <dt>{t("claim.scheda.fornitore")}</dt>
                    <dd>
                      {c.fornitore && puo("fornitore.vedi") ? <Link to={`/fornitori/${c.fornitore.id}`}>{c.fornitore.nome}</Link> : c.fornitoreTesto} <Ro>{t("comune.vendorhub")}</Ro>
                    </dd>
                    <dt>{t("claim.scheda.brand")}</dt>
                    <dd>{ro ? (c.brand ?? "—") : <Input value={b.brand ?? ""} onChange={(e) => set("brand", e.target.value)} maxLength={200} />}</dd>
                    <dt>{t("claim.scheda.problema")}</dt>
                    <dd>{ro ? (c.problema ?? "—") : <Input.TextArea autoSize value={b.problema ?? ""} onChange={(e) => set("problema", e.target.value)} maxLength={2000} />}</dd>
                    <dt>{t("claim.scheda.risoluzione")}</dt>
                    <dd>{ro ? (c.risoluzione ?? "—") : <Input.TextArea autoSize value={b.risoluzione ?? ""} onChange={(e) => set("risoluzione", e.target.value)} maxLength={2000} />}</dd>
                    <dt>{t("claim.scheda.valore")}</dt>
                    <dd>
                      {ro ? (
                        <span className="num" data-sensitive>{fmtImporto(c.importo, c.valuta)}</span>
                      ) : (
                        <Space.Compact style={{ width: "100%", maxWidth: 260 }}>
                          <InputNumber min={0} value={b.importo} onChange={(v) => set("importo", v ?? null)} formatter={numFormatter} parser={numParser} style={{ flex: 1 }} />
                          <Select value={b.valuta} onChange={(v) => set("valuta", v)} style={{ width: 90 }} options={VALUTE.map((v) => ({ value: v, label: v }))} />
                        </Space.Compact>
                      )}
                    </dd>
                    <dt>{t("claim.scheda.modalita")}</dt>
                    <dd>{ro ? (c.modalitaRecupero ?? <Miss>{t("claim.scheda.daDecidere")}</Miss>) : <SelectLista lista="modalita_recupero" value={b.modalitaRecupero} onChange={(v) => set("modalitaRecupero", v)} placeholder={t("claim.scheda.daDecidere")} style={{ maxWidth: 320 }} />}</dd>
                    <dt>{t("claim.scheda.recuperato")}</dt>
                    <dd>
                      {ro ? (
                        c.importoRecuperato !== null ? <span className="num" data-sensitive>{fmtImporto(c.importoRecuperato, c.valuta)}</span> : <Miss>{t("comune.daCompilare")}</Miss>
                      ) : (
                        <InputNumber min={0} value={b.importoRecuperato} onChange={(v) => set("importoRecuperato", v ?? null)} formatter={numFormatter} parser={numParser} addonAfter={b.valuta} style={{ width: "100%", maxWidth: 260 }} />
                      )}
                    </dd>
                    <dt>{t("claim.scheda.blocco")}</dt>
                    <dd data-tip={ANCORA.claimBlocco}>
                      {ro ? (
                        c.bloccoPagamento === null ? <Miss>{t("comune.nonIndicato")}</Miss> : c.bloccoPagamento ? t("claim.scheda.bloccoSi") : t("azioni.no")
                      ) : (
                        <Select
                          value={b.bloccoPagamento === null ? "nd" : b.bloccoPagamento ? "si" : "no"}
                          onChange={(x) => set("bloccoPagamento", x === "nd" ? null : x === "si")}
                          style={{ width: "100%", maxWidth: 260 }}
                          options={[
                            { value: "nd", label: t("comune.trattino") },
                            { value: "si", label: t("claim.scheda.bloccoSi") },
                            { value: "no", label: t("azioni.no") },
                          ]}
                        />
                      )}
                    </dd>
                    <dt>{t("claim.scheda.note")}</dt>
                    <dd>{ro ? (c.note ?? "—") : <Input.TextArea autoSize value={b.note ?? ""} onChange={(e) => set("note", e.target.value)} maxLength={2000} />}</dd>
                  </dl>
                  {c.statoOriginale && (
                    <p className="muted small" style={{ margin: "10px 0 0" }}>
                      {t("claim.scheda.statoOriginale", { s: c.statoOriginale })}
                    </p>
                  )}
                  {!ro && (
                    <Space style={{ marginTop: 12 }}>
                      <Button onClick={() => setB(iniziale)} disabled={!cambiato}>
                        {t("azioni.annulla")}
                      </Button>
                      <Button type="primary" disabled={!cambiato} loading={m.isPending} onClick={() => m.mutate(b)}>
                        {t("azioni.salva")}
                      </Button>
                    </Space>
                  )}
                </Card>
                <Card size="small" title={t("claim.scheda.storico")} style={{ marginTop: 16 }}>
                  <Storico righe={c.aggiornamenti} vuoto={t("claim.scheda.nessunoStorico")} />
                </Card>
              </Col>
              <Col xs={24} xl={8}>
                <Card size="small" title={t("claim.scheda.rfTitolo")} style={{ borderColor: palette.warn }}>
                  {rf ? (
                    <>
                      <p style={{ margin: "0 0 6px" }}>
                        <Link className="mono" to={`/redflag/segnalazioni/${rf.id}`}>
                          {rf.codice}
                        </Link>{" "}
                        · {rf.cliente}
                        {rf.progetto ? ` · ${rf.progetto}` : ""}
                      </p>
                      <Space>
                        <Sev s={rf.severita} />
                        <StatoRf s={rf.stato} />
                      </Space>
                    </>
                  ) : !c.segnalazioneProposta ? (
                    <Vuoto titolo={t("claim.scheda.nessunRf")} testo={t("claim.scheda.nessunRfNota")} />
                  ) : null}
                  {c.segnalazioneProposta && (
                    <p className="small" style={{ margin: "10px 0" }}>
                      <span className="mono">{c.segnalazioneProposta.codice}</span> <Prop>{t("comune.proposto")}</Prop>
                      <br />
                      <span className="muted">
                        {t("claim.scheda.rfProposto", { codice: c.segnalazioneProposta.proposta.codice, verifica: c.segnalazioneProposta.daVerificare ? t("claim.scheda.rfDaVerificare") : "" })}
                      </span>
                    </p>
                  )}
                  {puo("proposta.crea") && (
                    <div style={{ marginTop: 10 }} data-tip={ANCORA.claimCollega}>
                      <ProponiCollegamento c={c} altro={!!(rf || c.segnalazioneProposta)} />
                    </div>
                  )}
                </Card>
                <Card size="small" title={t("claim.scheda.documenti")} style={{ marginTop: 16 }}>
                  <div className="prev">{t("claim.scheda.documentiInArrivo")}</div>
                </Card>
              </Col>
            </Row>
          </>
        );
      }}
    </Stato>
  );
}
